import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { XpBar } from './XpBar';
import { useAuth } from '@/lib/auth';
import { useMenu } from '@/lib/useMenu';
import { ResourcesMenu } from './ResourcesMenu';
import { Menu, X, LogOut, LayoutDashboard, BookOpen, MessageCircle, Shield, Users, Zap, ExternalLink, Film } from 'lucide-react';

/**
 * Render a single dynamic menu link (custom page / internal / external).
 * Reused in both desktop nav and mobile drawer.
 */
const NavMenuLink = ({ item, className, onClick, withIcon = false }) => {
  const isExternal = item.link_type === 'external' || (item.href || '').startsWith('http');
  if (isExternal) {
    return (
      <a
        href={item.href}
        target={item.open_in_new_tab ? '_blank' : undefined}
        rel={item.open_in_new_tab ? 'noreferrer' : undefined}
        className={className}
        onClick={onClick}
        data-testid={`nav-custom-${item.id || item.label}`}
      >
        {item.label}{withIcon && item.open_in_new_tab && <ExternalLink size={11} className="inline-block ml-1 -mt-0.5 opacity-60"/>}
      </a>
    );
  }
  return (
    <Link
      to={item.href || '#'}
      target={item.open_in_new_tab ? '_blank' : undefined}
      className={className}
      onClick={onClick}
      data-testid={`nav-custom-${item.id || item.label}`}
    >
      {item.label}
    </Link>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { header: customLinks } = useMenu();

  const handleLogout = () => { logout(); navigate('/'); };

  // Unified solid header: white background, black ink text, gold hovers.
  const navCls = "sticky top-0 z-40 bg-white border-b border-kc shadow-[0_1px_0_rgba(0,0,0,0.04)] text-ink";
  const linkCls = "hover:text-gold transition-colors";

  return (
    <header className={navCls}>
      <div className="kc-container flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="header-logo-link" className="flex-shrink-0">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
          <Link to="/courses" className={linkCls} data-testid="nav-courses">Courses</Link>
          <Link to="/videos" className={linkCls} data-testid="nav-videos">Real Talk</Link>
          <Link to="/games" className={linkCls} data-testid="nav-games">Games</Link>
          <Link to="/community" className={linkCls} data-testid="nav-community">Community</Link>
          <ResourcesMenu />
          <Link to="/support" className={linkCls} data-testid="nav-support">Support</Link>
          <Link to="/about" className={linkCls} data-testid="nav-about">About</Link>
          {/* Admin-managed dynamic menu items (header) */}
          {customLinks.map((item) => (
            <NavMenuLink key={item.id} item={item} className={`${linkCls} inline-flex items-center`} withIcon/>
          ))}
          {user && (
            <>
              <Link to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</Link>
              <Link to="/coach" className={linkCls} data-testid="nav-coach">AI Coach</Link>
              {user.is_admin && <Link to="/admin" className={linkCls} data-testid="nav-admin">Admin</Link>}
            </>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <XpBar />
          {!user && (
            <>
              <Link to="/login" className="h-10 px-4 inline-flex items-center rounded-[var(--kc-radius-sm)] text-sm font-semibold hover:bg-surface-2 transition-colors" data-testid="header-login">Log in</Link>
              <Link to="/signup" className="h-10 px-5 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="header-signup">Get started</Link>
            </>
          )}
          {user && (
            <button onClick={handleLogout} className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] text-sm font-semibold hover:bg-surface-2 transition-colors" data-testid="header-logout">
              <LogOut size={16} /> Log out
            </button>
          )}
        </div>

        <button className="md:hidden p-2 -mr-1 rounded-[var(--kc-radius-sm)] hover:bg-surface-2 transition-colors text-ink" onClick={() => setOpen(!open)} aria-label="Open menu" data-testid="mobile-menu-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-kc bg-white">
          <div className="kc-container py-3 flex flex-col gap-1">
            <Link to="/courses" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-courses"><BookOpen size={16}/> Courses</Link>
            <Link to="/videos" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-videos"><Film size={16}/> Real Talk</Link>
            <Link to="/games" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-games"><Zap size={16}/> Games</Link>
            <Link to="/community" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-community"><Users size={16}/> Community</Link>
            <ResourcesMenu mobile onSelect={() => setOpen(false)} />
            <Link to="/about" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-about"><Shield size={16}/> About Mickey</Link>
            {customLinks.map((item) => (
              <NavMenuLink key={item.id} item={item} className="py-2 flex items-center gap-2" onClick={() => setOpen(false)} withIcon/>
            ))}
            {user && <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-dashboard"><LayoutDashboard size={16}/> Dashboard</Link>}
            {user && <Link to="/coach" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-coach"><MessageCircle size={16}/> AI Coach</Link>}
            {user?.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="py-2 flex items-center gap-2" data-testid="mnav-admin"><Shield size={16}/> Admin</Link>}
            <div className="h-px my-2 bg-kc" />
            {!user ? (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="py-2 font-semibold" data-testid="mnav-login">Log in</Link>
                <Link to="/signup" onClick={() => setOpen(false)} className="py-2 font-semibold text-[color:var(--kc-gold)]" data-testid="mnav-signup">Get started</Link>
              </>
            ) : (
              <button onClick={() => { setOpen(false); handleLogout(); }} className="py-2 text-left font-semibold flex items-center gap-2" data-testid="mnav-logout"><LogOut size={16}/> Log out</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
